import { useState, type ChangeEvent, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import QRCode from 'react-qr-code';
import {
    AlertCircle,
    Building2,
    Calendar,
    Camera,
    Check,
    CheckCircle,
    Clock,
    Copy,
    CreditCard,
    DollarSign,
    Eye,
    EyeOff,
    FileText,
    Image,
    Lock,
    MapPin,
    Plus,
    QrCode,
    Receipt,
    Search,
    Sparkles,
    Tag,
    Trash2,
    TrendingUp,
    Users,
    Wallet,
    X,
    XCircle,
    type LucideIcon,
} from 'lucide-react';
import AddressPicker from '../../components/AddressPicker';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import type { Merchant } from '../../contexts/MerchantContextCore';
import {
    getPromotionDiscountSummary,
    promotionIsExclusive,
    promotionUsesLevelDiscounts,
    type MerchantRecentSale,
    type MerchantStats,
    type PlatformFeeSummary,
    type PlatformPaymentRow,
    type Promotion,
} from '../../hooks/useData';

export type MerchantTabId = 'dashboard' | 'finance' | 'validate' | 'promotions' | 'media' | 'settings';
export type PromotionDiscountMode = 'levels' | 'custom';
export type CustomPromoVisibility = 'public' | 'secret';

export interface NewPromotion {
    title: string;
    discountMode: PromotionDiscountMode;
    customDiscountName: string;
    customDiscountPercent: number;
    customVisibility: CustomPromoVisibility;
    exclusiveCouponCode: string;
    discountBronze: number;
    discountSilver: number;
    discountGold: number;
    maxUses: number | null;
    maxUsesPerUser: number | null;
    endDate: string;
}

export interface ValidationResult {
    success: boolean;
    message: string;
    redemption?: {
        code: string;
        userName: string;
        discount: string;
        title: string;
        usedAt: string;
        purchaseAmount?: number;
        discountSaved?: number;
    };
}

export interface PixPaymentPayload {
    paymentId: string;
    amount: number;
    pending: number;
    status: PlatformPaymentRow['status'];
    mpOrderId: string | null;
    mpPaymentId: string | null;
    expiresAt: string | null;
    qrCode: string | null;
    qrCodeBase64: string | null;
    ticketUrl: string | null;
}

function money(value: unknown) {
    return `R$ ${(Number(value) || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('pt-BR');
}

function platformPaymentStatusLabel(status: PlatformPaymentRow['status']): {
    text: string;
    tone: 'ok' | 'pending' | 'failed';
    Icon: LucideIcon;
} {
    switch (status) {
        case 'approved':
            return { text: 'Pago', tone: 'ok', Icon: CheckCircle };
        case 'rejected':
            return { text: 'Recusado', tone: 'failed', Icon: AlertCircle };
        case 'cancelled':
            return { text: 'Cancelado', tone: 'failed', Icon: XCircle };
        case 'in_process':
            return { text: 'Em processamento', tone: 'pending', Icon: Clock };
        default:
            return { text: 'Aguardando', tone: 'pending', Icon: Clock };
    }
}

function MerchantPanel({
    title,
    description,
    icon,
    children,
    action,
    className = '',
}: {
    title: string;
    description?: string;
    icon?: ReactNode;
    children: ReactNode;
    action?: ReactNode;
    className?: string;
}) {
    return (
        <section className={`merchant-panel-card merchant-premium-panel ${className}`}>
            <div className="merchant-panel-head">
                <div>
                    <h3 className="merchant-card-title">
                        {icon}
                        {title}
                    </h3>
                    {description && <p className="merchant-section-desc">{description}</p>}
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

function MetricCard({
    label,
    value,
    hint,
    icon,
    tone = 'neutral',
}: {
    label: string;
    value: string;
    hint?: string;
    icon: ReactNode;
    tone?: 'neutral' | 'positive' | 'warning';
}) {
    return (
        <div className={`merchant-metric-card merchant-metric-card--${tone}`}>
            <div className="merchant-metric-icon">{icon}</div>
            <span className="merchant-metric-value">{value}</span>
            <span className="merchant-metric-label">{label}</span>
            {hint && <span className="merchant-metric-hint">{hint}</span>}
        </div>
    );
}

function ActionButton({
    icon,
    label,
    hint,
    onClick,
}: {
    icon: ReactNode;
    label: string;
    hint: string;
    onClick: () => void;
}) {
    return (
        <button type="button" className="merchant-action-card" onClick={onClick}>
            <span className="merchant-action-icon">{icon}</span>
            <span className="merchant-action-copy">
                <strong>{label}</strong>
                <small>{hint}</small>
            </span>
        </button>
    );
}

function EmptyState({ title, text }: { title: string; text: string }) {
    return (
        <div className="merchant-empty-state">
            <span>{title}</span>
            <p>{text}</p>
        </div>
    );
}

function StatusPill({ active }: { active: boolean }) {
    return <span className={`promo-status ${active ? 'active' : ''}`}>{active ? 'Ativa' : 'Encerrada'}</span>;
}

function PlatformPaymentItem({ payment }: { payment: PlatformPaymentRow }) {
    const status = platformPaymentStatusLabel(payment.status);
    return (
        <li className={`platform-fee-history-item platform-fee-history-item--${status.tone}`}>
            <div className="platform-fee-history-main">
                <span className="platform-fee-history-amount">{money(payment.amount)}</span>
                <span className="platform-fee-history-date">{new Date(payment.createdAt).toLocaleString('pt-BR')}</span>
                <span className="platform-fee-history-method">{payment.paymentMethod === 'pix' ? 'Pix' : 'Mercado Pago'}</span>
            </div>
            <span className={`platform-fee-history-status platform-fee-history-status--${status.tone}`}>
                <status.Icon size={14} /> {status.text}
            </span>
        </li>
    );
}

export function MerchantDashboardTab({
    stats,
    feeSummary,
    promotions,
    onNavigate,
    onCreatePromotion,
}: {
    stats: MerchantStats | null;
    feeSummary: PlatformFeeSummary | null;
    promotions: Promotion[];
    onNavigate: (tab: MerchantTabId) => void;
    onCreatePromotion: () => void;
}) {
    const activePromotions = promotions.filter((promo) => promo.active);
    const featuredPromotions = activePromotions.slice(0, 3);

    return (
        <div className="merchant-tab-content merchant-dashboard-tab">
            <section className="merchant-today-card">
                <div className="merchant-today-copy">
                    <span className="merchant-eyebrow">Resumo de hoje</span>
                    <h3>Gestão do parceiro</h3>
                    <p>Acompanhe vendas, valide benefícios e mantenha sua vitrine atualizada.</p>
                </div>
                <div className="merchant-today-amount">
                    <small>Vendas pelo app</small>
                    <strong>{money(stats?.totalPurchaseAmount)}</strong>
                </div>
            </section>

            <div className="merchant-metrics-grid">
                <MetricCard label="Cupons usados" value={String(stats?.couponsUsed ?? 0)} icon={<QrCode size={20} />} tone="positive" />
                <MetricCard label="Novos clientes" value={String(stats?.newCustomers ?? 0)} icon={<Users size={20} />} />
                <MetricCard label="Visualizações" value={String(stats?.views ?? 0)} icon={<Eye size={20} />} />
                <MetricCard
                    label="Taxa pendente"
                    value={money(feeSummary?.pending)}
                    hint={feeSummary && feeSummary.pending <= 0 ? 'Sem cobrança agora' : 'Disponível no financeiro'}
                    icon={<CreditCard size={20} />}
                    tone={feeSummary && feeSummary.pending > 0 ? 'warning' : 'neutral'}
                />
            </div>

            <div className="merchant-quick-actions">
                <ActionButton icon={<QrCode size={22} />} label="Validar cupom" hint="Fluxo rápido de balcão" onClick={() => onNavigate('validate')} />
                <ActionButton icon={<Plus size={22} />} label="Criar promoção" hint="Oferta pública ou secreta" onClick={onCreatePromotion} />
                <ActionButton icon={<Image size={22} />} label="Atualizar mídia" hint="Capa, galeria e vídeo" onClick={() => onNavigate('media')} />
            </div>

            <MerchantPanel
                title="Promoções ativas"
                description="As campanhas que o turista encontra no app."
                icon={<Tag size={20} aria-hidden />}
                action={<button type="button" className="merchant-panel-link" onClick={() => onNavigate('promotions')}>Ver todas</button>}
            >
                {featuredPromotions.length === 0 ? (
                    <EmptyState title="Nenhuma promoção ativa" text="Crie uma promoção para aparecer na página do seu estabelecimento." />
                ) : (
                    <div className="activity-list">
                        {featuredPromotions.map((promo) => (
                            <div key={promo.id} className="activity-item merchant-activity-item">
                                <span className="activity-time">{getPromotionDiscountSummary(promo)}</span>
                                <span className="activity-event">{promo.title}</span>
                                <span className="activity-user">{promo.currentUses}/{promo.maxUses}</span>
                            </div>
                        ))}
                    </div>
                )}
            </MerchantPanel>
        </div>
    );
}

export function MerchantFinanceTab({
    stats,
    feeSummary,
    feeLoading,
    recentSales,
    onOpenFeeDetails,
}: {
    stats: MerchantStats | null;
    feeSummary: PlatformFeeSummary | null;
    feeLoading: boolean;
    recentSales: MerchantRecentSale[];
    onOpenFeeDetails: () => void;
}) {
    const netRevenue = Math.max(0, (Number(stats?.totalPurchaseAmount) || 0) - (Number(stats?.platformFee) || 0));
    const hasPendingFee = !!feeSummary && feeSummary.pending > 0;

    return (
        <div className="merchant-tab-content merchant-finance-tab">
            <div className="merchant-section-head">
                <h2 className="merchant-section-title">
                    <Wallet size={22} strokeWidth={2} aria-hidden />
                    Financeiro
                </h2>
                <p className="merchant-section-desc">Vendas e taxa da plataforma pelo uso do app.</p>
            </div>

            <div className="finance-cards merchant-finance-summary">
                <MetricCard label="Vendas pelo app" value={money(stats?.totalPurchaseAmount)} hint="Cupons validados" icon={<TrendingUp size={20} />} tone="positive" />
                <MetricCard label={`Taxa (${stats?.commissionRate ?? feeSummary?.commissionRate ?? 10}%)`} value={money(stats?.platformFee ?? feeSummary?.totalFee)} hint="A pagar ao Facilita Pass+" icon={<Receipt size={20} />} />
                <MetricCard label="Receita líquida" value={money(netRevenue)} hint="Vendas menos taxa" icon={<DollarSign size={20} />} />
            </div>

            <MerchantPanel
                title="Pagamento da taxa"
                description={`Cobramos ${feeSummary?.commissionRate ?? stats?.commissionRate ?? 10}% das vendas validadas pelo app.`}
                icon={<CreditCard size={20} aria-hidden />}
                className="platform-fee-card"
            >
                <div className="platform-fee-compact">
                    <div className="platform-fee-compact-main">
                        <span className="platform-fee-compact-label">Pendente agora</span>
                        <strong>{money(feeSummary?.pending)}</strong>
                        <small>{hasPendingFee ? 'Disponível para pagamento via Pix.' : 'Sem cobranças pendentes no momento.'}</small>
                    </div>
                    <div className="platform-fee-compact-side">
                        <span>Total: <strong>{money(feeSummary?.totalFee)}</strong></span>
                        <span>Pago: <strong>{money(feeSummary?.paid)}</strong></span>
                    </div>
                </div>
                <div className="platform-fee-action platform-fee-action--compact">
                    <Button onClick={onOpenFeeDetails} disabled={feeLoading || !feeSummary}>
                        <Receipt size={18} /> Ver detalhes da taxa
                    </Button>
                    {!hasPendingFee && !feeLoading && (
                        <span className="platform-fee-status platform-fee-status--ok">
                            <CheckCircle size={16} /> Sem cobranças pendentes
                        </span>
                    )}
                </div>
                {recentSales.length > 0 && (
                    <p className="platform-fee-compact-hint">
                        Última venda validada: {recentSales[0]?.usedAt ? new Date(recentSales[0].usedAt).toLocaleString('pt-BR') : 'recente'}
                    </p>
                )}
            </MerchantPanel>

            <MerchantPanel title="Últimas vendas" description="Validações recentes feitas pelo portal." icon={<Receipt size={18} aria-hidden />}>
                {recentSales.length === 0 ? (
                    <EmptyState title="Nenhuma venda registrada ainda" text="As vendas aparecem aqui depois que os cupons forem validados." />
                ) : (
                    <div className="recent-sales-list">
                        {recentSales.map((sale) => (
                            <div key={sale.id} className="recent-sale-item">
                                <div className="recent-sale-main">
                                    <span className="recent-sale-user">{sale.userName}</span>
                                    <span className="recent-sale-date">{sale.usedAt ? new Date(sale.usedAt).toLocaleString('pt-BR') : '-'}</span>
                                </div>
                                <div className="recent-sale-details">
                                    {sale.promotionTitle && <span className="recent-sale-promo">{sale.promotionTitle} ({sale.promotionDiscount})</span>}
                                    {sale.purchaseAmount != null && <span className="recent-sale-amount">{money(sale.purchaseAmount)}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </MerchantPanel>

            <MerchantPanel title="Por que usar o app" icon={<Sparkles size={20} aria-hidden />} className="merchant-benefits-card">
                <ul className="benefits-list">
                    <li><strong>{stats?.newCustomers ?? 0}</strong> novos clientes chegaram até você pelo app</li>
                    <li><strong>{stats?.couponsUsed ?? 0}</strong> cupons já validados nesta loja</li>
                    <li>Seu estabelecimento foi visto <strong>{stats?.views ?? 0}</strong> vezes no app</li>
                </ul>
            </MerchantPanel>
        </div>
    );
}

export function MerchantPlatformFeeDetailsModal({
    isOpen,
    stats,
    feeSummary,
    platformPayments,
    recentSales,
    feeLoading,
    creatingPayment,
    onClose,
    onGeneratePix,
}: {
    isOpen: boolean;
    stats: MerchantStats | null;
    feeSummary: PlatformFeeSummary | null;
    platformPayments: PlatformPaymentRow[];
    recentSales: MerchantRecentSale[];
    feeLoading: boolean;
    creatingPayment: boolean;
    onClose: () => void;
    onGeneratePix: () => void;
}) {
    const commissionRate = feeSummary?.commissionRate ?? stats?.commissionRate ?? 10;
    const netRevenue = Math.max(0, (Number(stats?.totalPurchaseAmount) || 0) - (Number(stats?.platformFee) || 0));
    const canGeneratePix = !!feeSummary && feeSummary.pending > 0 && !feeLoading && !creatingPayment;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Detalhes da taxa"
            contentClassName="merchant-fee-details-shell"
            footer={(
                <>
                    <Button variant="ghost" onClick={onClose}>Fechar</Button>
                    <Button onClick={onGeneratePix} disabled={!canGeneratePix}>
                        {creatingPayment ? 'Gerando Pix...' : <><QrCode size={18} /> Gerar Pix</>}
                    </Button>
                </>
            )}
        >
            <div className="platform-fee-details-modal">
                <section className="platform-fee-details-hero">
                    <span className="merchant-eyebrow">Taxa da plataforma</span>
                    <strong>{money(feeSummary?.pending)}</strong>
                    <p>
                        {feeSummary && feeSummary.pending > 0
                            ? 'Valor pendente para pagamento via Pix.'
                            : 'Tudo certo: não há taxa pendente agora.'}
                    </p>
                </section>

                <div className="platform-fee-detail-grid">
                    <div className="platform-fee-detail-card">
                        <span>Total acumulado</span>
                        <strong>{money(feeSummary?.totalFee)}</strong>
                    </div>
                    <div className="platform-fee-detail-card">
                        <span>Já pago</span>
                        <strong>{money(feeSummary?.paid)}</strong>
                    </div>
                    <div className="platform-fee-detail-card platform-fee-detail-card--pending">
                        <span>Pendente</span>
                        <strong>{money(feeSummary?.pending)}</strong>
                    </div>
                    <div className="platform-fee-detail-card">
                        <span>Comissão aplicada</span>
                        <strong>{commissionRate}%</strong>
                    </div>
                    <div className="platform-fee-detail-card">
                        <span>Vendas pelo app</span>
                        <strong>{money(stats?.totalPurchaseAmount)}</strong>
                    </div>
                    <div className="platform-fee-detail-card">
                        <span>Receita líquida estimada</span>
                        <strong>{money(netRevenue)}</strong>
                    </div>
                </div>

                <section className="platform-fee-details-section">
                    <h4>Últimas vendas pelo app</h4>
                    {recentSales.length === 0 ? (
                        <EmptyState title="Nenhuma venda registrada ainda" text="As vendas aparecem depois da validação de cupons." />
                    ) : (
                        <div className="recent-sales-list platform-fee-modal-sales">
                            {recentSales.slice(0, 5).map((sale) => (
                                <div key={sale.id} className="recent-sale-item">
                                    <div className="recent-sale-main">
                                        <span className="recent-sale-user">{sale.userName}</span>
                                        <span className="recent-sale-date">{sale.usedAt ? new Date(sale.usedAt).toLocaleString('pt-BR') : '-'}</span>
                                    </div>
                                    <div className="recent-sale-details">
                                        {sale.promotionTitle && <span className="recent-sale-promo">{sale.promotionTitle} ({sale.promotionDiscount})</span>}
                                        {sale.purchaseAmount != null && <span className="recent-sale-amount">{money(sale.purchaseAmount)}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                <section className="platform-fee-details-section">
                    <h4>Histórico de pagamentos</h4>
                    {platformPayments.length === 0 ? (
                        <EmptyState title="Nenhum pagamento registrado" text="Quando houver pagamentos, eles aparecem aqui com status e data." />
                    ) : (
                        <ul className="platform-fee-history-list platform-fee-history-list--modal">
                            {platformPayments.map((payment) => <PlatformPaymentItem key={payment.id} payment={payment} />)}
                        </ul>
                    )}
                </section>
            </div>
        </Modal>
    );
}

export function MerchantValidateTab({
    validationPurchaseAmount,
    onPurchaseAmountChange,
    scannerActive,
    onStartScanner,
    onStopScanner,
    couponCode,
    onCouponCodeChange,
    onValidateCoupon,
    validating,
    validationError,
    validationResult,
}: {
    validationPurchaseAmount: string;
    onPurchaseAmountChange: (value: string) => void;
    scannerActive: boolean;
    onStartScanner: () => void;
    onStopScanner: () => void;
    couponCode: string;
    onCouponCodeChange: (value: string) => void;
    onValidateCoupon: () => void;
    validating: boolean;
    validationError: string;
    validationResult: ValidationResult | null;
}) {
    return (
        <div className="merchant-tab-content">
            <section className="merchant-validate-card merchant-counter-card">
                <span className="merchant-eyebrow">Atendimento no balcão</span>
                <h3 className="merchant-counter-title">Validar cupom</h3>
                <p className="merchant-help-text">Informe o valor da compra antes de escanear ou digitar o código do cliente.</p>

                <div className="merchant-purchase-field merchant-purchase-field--primary">
                    <label htmlFor="merchant-purchase-amount" className="merchant-purchase-label">Valor total da compra</label>
                    <div className="merchant-currency-input">
                        <span>R$</span>
                        <input
                            id="merchant-purchase-amount"
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder="89,90"
                            value={validationPurchaseAmount}
                            onChange={(e) => onPurchaseAmountChange(e.target.value)}
                            className="merchant-purchase-input"
                        />
                    </div>
                </div>

                <div className="scanner-section merchant-scanner-section">
                    {!scannerActive ? (
                        <button className="scan-btn merchant-primary-scan-btn" onClick={onStartScanner}>
                            <Camera size={24} />
                            <span>Escanear QR Code</span>
                        </button>
                    ) : (
                        <div className="scanner-container">
                            <div className="merchant-scanner-header">
                                <span>Aponte para o QR Code</span>
                                <button type="button" className="merchant-scanner-close" onClick={onStopScanner} aria-label="Fechar câmera">
                                    <X size={20} />
                                </button>
                            </div>
                            <div id="qr-reader" className="qr-reader"></div>
                        </div>
                    )}
                </div>

                <div className="merchant-divider"><span>ou digite o código</span></div>
                <div className="merchant-validate-input-group">
                    <input
                        type="text"
                        placeholder="Ex: A3F7B2C1"
                        value={couponCode}
                        onChange={(e) => onCouponCodeChange(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === 'Enter' && onValidateCoupon()}
                        maxLength={8}
                        className="merchant-code-input"
                    />
                    <Button onClick={onValidateCoupon} disabled={validating} className="merchant-validate-btn">
                        {validating ? 'Validando...' : <><Search size={18} /> Validar</>}
                    </Button>
                </div>

                {validationError && (
                    <div className="validation-result error" role="alert" aria-live="assertive">
                        <XCircle size={48} />
                        <h4>Não foi possível validar</h4>
                        <p>{validationError}</p>
                    </div>
                )}
                {validationResult?.success && (
                    <div className="validation-result success" role="status" aria-live="polite">
                        <CheckCircle size={48} />
                        <h4>Cupom validado</h4>
                        <div className="validation-details">
                            <div className="detail-row"><span>Cliente:</span><strong>{validationResult.redemption?.userName}</strong></div>
                            <div className="detail-row"><span>Promoção:</span><strong>{validationResult.redemption?.title}</strong></div>
                            <div className="detail-row"><span>Desconto:</span><strong className="discount-value">{validationResult.redemption?.discount}</strong></div>
                            <div className="detail-row"><span>Código:</span><strong>{validationResult.redemption?.code}</strong></div>
                            {validationResult.redemption?.purchaseAmount && (
                                <div className="detail-row merchant-detail-row merchant-detail-row--muted">
                                    <span>Valor da compra</span>
                                    <strong>{money(validationResult.redemption.purchaseAmount)}</strong>
                                </div>
                            )}
                            {validationResult.redemption?.discountSaved && (
                                <div className="detail-row merchant-detail-row merchant-detail-row--success">
                                    <span>Economia do cliente</span>
                                    <strong>{money(validationResult.redemption.discountSaved)}</strong>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}

export function MerchantPromotionsTab({
    promotions,
    promoLoading,
    onCreateClick,
    onDelete,
}: {
    promotions: Promotion[];
    promoLoading: boolean;
    onCreateClick: () => void;
    onDelete: (id: string) => void;
}) {
    return (
        <div className="merchant-tab-content">
            <div className="merchant-promotions-head merchant-premium-head">
                <div>
                    <h3 className="merchant-section-title merchant-section-title--plain">Suas promoções</h3>
                    <p className="merchant-section-desc">Crie ofertas públicas ou cupons secretos para campanhas específicas.</p>
                </div>
                <Button className="add-promo-btn" onClick={onCreateClick}><Plus size={18} />Nova</Button>
            </div>

            <div className="promotions-list">
                {promoLoading ? (
                    <p>Carregando promoções...</p>
                ) : promotions.length === 0 ? (
                    <EmptyState title="Nenhuma promoção cadastrada" text="Comece com uma oferta simples e acompanhe os resgates aqui." />
                ) : (
                    promotions.map((promo) => {
                        const progress = promo.maxUses > 0 ? Math.min(100, (promo.currentUses / promo.maxUses) * 100) : 0;
                        return (
                            <div key={promo.id} className={`promo-card merchant-promo-card ${!promo.active ? 'inactive' : ''}`}>
                                <div className="promo-header">
                                    <div className="promo-title-row">
                                        <h4>{promo.title}</h4>
                                        {!promotionUsesLevelDiscounts(promo) && <span className="promo-badge promo-badge--custom">Personalizado</span>}
                                        {promotionIsExclusive(promo) && <span className="promo-badge promo-badge--exclusive">Só com código</span>}
                                    </div>
                                    <div className="promo-actions">
                                        <StatusPill active={promo.active} />
                                        <button className="delete-promo-btn" onClick={() => onDelete(promo.id)} aria-label={`Excluir promoção ${promo.title}`}>
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                                <div className="promo-discount">{getPromotionDiscountSummary(promo)}</div>
                                <div className="promo-stats">
                                    <div className="promo-stat"><QrCode size={14} /><span>{promo.currentUses}/{promo.maxUses} usos</span></div>
                                    {promo.maxUsesPerUser != null && promo.maxUsesPerUser > 0 && (
                                        <div className="promo-stat"><Users size={14} /><span>Máx. {promo.maxUsesPerUser}/cliente</span></div>
                                    )}
                                    <div className="promo-stat"><Calendar size={14} /><span>Até {formatDate(promo.endDate)}</span></div>
                                </div>
                                <div className="promo-progress"><div className="promo-progress-fill" style={{ width: `${progress}%` }} /></div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export function MerchantPromotionModal({
    isOpen,
    promotion,
    onChange,
    onClose,
    onSubmit,
}: {
    isOpen: boolean;
    promotion: NewPromotion;
    onChange: Dispatch<SetStateAction<NewPromotion>>;
    onClose: () => void;
    onSubmit: () => void;
}) {
    const [step, setStep] = useState(0);
    const stepLabels = ['Identificação', 'Desconto', 'Limites', 'Prévia'];
    const lastStep = stepLabels.length - 1;

    const close = () => {
        setStep(0);
        onClose();
    };

    const update = (patch: Partial<NewPromotion>) => onChange((current) => ({ ...current, ...patch }));

    return (
        <Modal
            isOpen={isOpen}
            onClose={close}
            title="Nova promoção"
            footer={(
                <>
                    <Button variant="ghost" onClick={step === 0 ? close : () => setStep((current) => Math.max(0, current - 1))}>
                        {step === 0 ? 'Cancelar' : 'Voltar'}
                    </Button>
                    <Button onClick={step === lastStep ? onSubmit : () => setStep((current) => Math.min(lastStep, current + 1))}>
                        {step === lastStep ? 'Criar promoção' : 'Continuar'}
                    </Button>
                </>
            )}
        >
            <div className="promo-wizard">
                <div className="promo-wizard-steps" aria-label="Etapas da promoção">
                    {stepLabels.map((label, index) => (
                        <button
                            key={label}
                            type="button"
                            className={index === step ? 'active' : index < step ? 'complete' : ''}
                            onClick={() => setStep(index)}
                        >
                            <span>{index + 1}</span>
                            {label}
                        </button>
                    ))}
                </div>

                {step === 0 && (
                    <div className="promo-wizard-page">
                        <label htmlFor="promo-title">Título da promoção</label>
                        <input
                            id="promo-title"
                            type="text"
                            placeholder="Ex.: Happy hour, Semana do cliente..."
                            value={promotion.title}
                            onChange={(e) => update({ title: e.target.value })}
                            autoComplete="off"
                        />
                        <p className="promo-field-empty-hint">Esse nome aparece para você e para o turista quando a promoção for pública.</p>
                    </div>
                )}

                {step === 1 && (
                    <div className="promo-wizard-page">
                        <div className="promo-discount-mode" role="group" aria-label="Tipo de desconto">
                            <span className="promo-discount-mode-label">Tipo de desconto</span>
                            <div className="promo-discount-mode-toggle">
                                <button type="button" className={promotion.discountMode === 'levels' ? 'active' : ''} onClick={() => update({ discountMode: 'levels' })}>
                                    Por níveis
                                </button>
                                <button type="button" className={promotion.discountMode === 'custom' ? 'active' : ''} onClick={() => update({ discountMode: 'custom' })}>
                                    Personalizado
                                </button>
                            </div>
                        </div>

                        {promotion.discountMode === 'levels' ? (
                            <div className="promo-level-discounts">
                                <label>Desconto por nível do cliente</label>
                                <div className="level-discount-row">
                                    <span className="level-label bronze">Bronze</span>
                                    <select value={promotion.discountBronze} onChange={(e) => update({ discountBronze: parseInt(e.target.value, 10) })}>
                                        {[3, 4, 5].map((n) => <option key={n} value={n}>{n}%</option>)}
                                    </select>
                                </div>
                                <div className="level-discount-row">
                                    <span className="level-label silver">Prata</span>
                                    <select value={promotion.discountSilver} onChange={(e) => update({ discountSilver: parseInt(e.target.value, 10) })}>
                                        {[5, 6, 7].map((n) => <option key={n} value={n}>{n}%</option>)}
                                    </select>
                                </div>
                                <div className="level-discount-row">
                                    <span className="level-label gold">Ouro</span>
                                    <select value={promotion.discountGold} onChange={(e) => update({ discountGold: parseInt(e.target.value, 10) })}>
                                        {[7, 8, 9, 10].map((n) => <option key={n} value={n}>{n}%</option>)}
                                    </select>
                                </div>
                            </div>
                        ) : (
                            <div className="promo-custom-discount">
                                <label htmlFor="custom-discount-name">Texto do desconto no app</label>
                                <input
                                    id="custom-discount-name"
                                    type="text"
                                    maxLength={120}
                                    value={promotion.customDiscountName}
                                    onChange={(e) => update({ customDiscountName: e.target.value })}
                                    placeholder="Opcional: se vazio, usa o título"
                                    autoComplete="off"
                                />
                                <label htmlFor="custom-discount-pct">Percentual para cálculo da economia</label>
                                <input
                                    id="custom-discount-pct"
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={promotion.customDiscountPercent}
                                    onChange={(e) => update({ customDiscountPercent: Math.min(100, Math.max(1, parseInt(e.target.value, 10) || 1)) })}
                                />
                                <div className="promo-discount-mode promo-discount-mode--nested" role="group" aria-label="Visibilidade no app">
                                    <span className="promo-discount-mode-label">Visibilidade</span>
                                    <div className="promo-discount-mode-toggle">
                                        <button type="button" className={promotion.customVisibility === 'public' ? 'active' : ''} onClick={() => update({ customVisibility: 'public' })}>
                                            Pública
                                        </button>
                                        <button type="button" className={promotion.customVisibility === 'secret' ? 'active' : ''} onClick={() => update({ customVisibility: 'secret' })}>
                                            Código secreto
                                        </button>
                                    </div>
                                </div>
                                {promotion.customVisibility === 'secret' && (
                                    <>
                                        <label htmlFor="exclusive-coupon-code">Código secreto</label>
                                        <input
                                            id="exclusive-coupon-code"
                                            type="text"
                                            maxLength={64}
                                            value={promotion.exclusiveCouponCode}
                                            onChange={(e) => update({ exclusiveCouponCode: e.target.value })}
                                            placeholder="Ex.: FACILITA2026"
                                            autoComplete="off"
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {step === 2 && (
                    <div className="promo-wizard-page promo-form-limits" role="group" aria-label="Limites e validade">
                        <div className="promo-form-field">
                            <label htmlFor="promo-max-uses">Máximo de usos total</label>
                            <input
                                id="promo-max-uses"
                                type="number"
                                min={1}
                                step={1}
                                inputMode="numeric"
                                placeholder="Ex.: 100"
                                value={promotion.maxUses == null ? '' : String(promotion.maxUses)}
                                onChange={(e) => update({ maxUses: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                            />
                        </div>
                        <div className="promo-form-field">
                            <label htmlFor="promo-max-per-user">Máximo por cliente</label>
                            <input
                                id="promo-max-per-user"
                                type="number"
                                min={1}
                                step={1}
                                inputMode="numeric"
                                placeholder="Ex.: 1"
                                value={promotion.maxUsesPerUser == null ? '' : String(promotion.maxUsesPerUser)}
                                onChange={(e) => update({ maxUsesPerUser: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                            />
                        </div>
                        <div className="promo-form-field">
                            <label htmlFor="promo-end-date">Data de término</label>
                            <input id="promo-end-date" type="date" value={promotion.endDate} onChange={(e) => update({ endDate: e.target.value })} />
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="promo-wizard-page">
                        <div className="promo-preview-card">
                            <span className="merchant-eyebrow">Prévia no app</span>
                            <h4>{promotion.title.trim() || 'Título da promoção'}</h4>
                            <p>
                                {promotion.discountMode === 'levels'
                                    ? `Bronze ${promotion.discountBronze}% | Prata ${promotion.discountSilver}% | Ouro ${promotion.discountGold}%`
                                    : `${promotion.customDiscountName.trim() || promotion.title.trim() || 'Oferta especial'} · ${promotion.customDiscountPercent}%`}
                            </p>
                            <div className="promo-preview-meta">
                                <span>{promotion.maxUses ?? 0} usos totais</span>
                                <span>Máx. {promotion.maxUsesPerUser ?? 0}/cliente</span>
                                <span>{promotion.endDate ? `Até ${formatDate(promotion.endDate)}` : 'Sem data definida'}</span>
                            </div>
                            {promotion.discountMode === 'custom' && promotion.customVisibility === 'secret' && (
                                <strong className="promo-preview-secret">Oculta: só resgata com código</strong>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}

export function MerchantMediaTab({
    coverPhoto,
    gallery,
    video,
    uploading,
    getImageUrl,
    onFileUpload,
    onRemoveImage,
    onVideoChange,
    onSaveMedia,
}: {
    coverPhoto: string;
    gallery: string[];
    video: string;
    uploading: boolean;
    getImageUrl: (url: string) => string;
    onFileUpload: (event: ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'cover') => void;
    onRemoveImage: (index: number) => void;
    onVideoChange: (value: string) => void;
    onSaveMedia: () => void;
}) {
    return (
        <div className="merchant-tab-content">
            <MerchantPanel title="Foto de capa" description="Esta imagem aparece nas listas e no topo da página do seu estabelecimento." icon={<Image size={20} aria-hidden />}>
                <div className="merchant-cover-section">
                    {coverPhoto ? (
                        <div className="merchant-cover-preview">
                            <img src={getImageUrl(coverPhoto)} alt="Capa do estabelecimento" className="merchant-cover-img" />
                            <div className="merchant-cover-actions">
                                <label className="merchant-upload-primary">
                                    <input type="file" accept="image/*" className="hidden-input" onChange={(e) => onFileUpload(e, 'cover')} disabled={uploading} />
                                    <Camera size={16} aria-hidden />
                                    Alterar capa
                                </label>
                            </div>
                        </div>
                    ) : (
                        <div className="merchant-cover-placeholder">
                            <label className="merchant-upload-dashed">
                                <input type="file" accept="image/*" className="hidden-input" onChange={(e) => onFileUpload(e, 'cover')} disabled={uploading} />
                                <Image size={32} aria-hidden />
                                <span>Adicionar foto de capa</span>
                            </label>
                        </div>
                    )}
                </div>
            </MerchantPanel>

            <MerchantPanel title="Galeria de fotos" description="Mostre ambiente, produtos e experiências reais.">
                <div className="media-gallery">
                    <div className="gallery-grid">
                        {gallery.map((url, index) => (
                            <div key={url || index} className="gallery-item">
                                <img src={getImageUrl(url)} alt={`Foto ${index + 1}`} />
                                <button className="remove-media-btn" onClick={() => onRemoveImage(index)} aria-label={`Remover foto ${index + 1}`}>
                                    <X size={16} />
                                </button>
                            </div>
                        ))}
                        <label className="add-media-btn">
                            <input type="file" accept="image/*" className="hidden-input" onChange={(e) => onFileUpload(e, 'image')} disabled={uploading} />
                            {uploading ? '...' : <Plus size={24} />}
                            <span>Adicionar foto</span>
                        </label>
                    </div>
                </div>
            </MerchantPanel>

            <MerchantPanel title="Vídeo de apresentação" description="Opcional, mas ajuda o turista a entender a experiência antes de chegar.">
                <div className="video-section">
                    {video ? (
                        <div className="video-preview">
                            <video src={getImageUrl(video)} controls />
                            <button className="remove-video-btn" onClick={() => onVideoChange('')}><Trash2 size={18} /> Remover vídeo</button>
                        </div>
                    ) : (
                        <div className="video-placeholder">
                            <label className="upload-video-btn">
                                <input type="file" accept="video/*" className="hidden-input" onChange={(e) => onFileUpload(e, 'video')} disabled={uploading} />
                                <Camera size={32} />
                                <span>Carregar vídeo</span>
                            </label>
                        </div>
                    )}
                </div>
            </MerchantPanel>

            <div className="merchant-media-save">
                <Button fullWidth onClick={onSaveMedia} disabled={uploading}>{uploading ? 'Salvando...' : 'Salvar alterações'}</Button>
            </div>
        </div>
    );
}

export function MerchantSettingsTab({
    merchant,
    establishmentNameDraft,
    onEstablishmentNameChange,
    nameChangePassword,
    onNameChangePasswordChange,
    showNameChangePassword,
    onToggleNamePassword,
    updatingName,
    onUpdateName,
    establishmentDescriptionDraft,
    onDescriptionChange,
    updatingDescription,
    onUpdateDescription,
    locationData,
    onLocationChange,
    updatingLocation,
    onUpdateLocation,
    onLogout,
}: {
    merchant: Merchant;
    establishmentNameDraft: string;
    onEstablishmentNameChange: (value: string) => void;
    nameChangePassword: string;
    onNameChangePasswordChange: (value: string) => void;
    showNameChangePassword: boolean;
    onToggleNamePassword: () => void;
    updatingName: boolean;
    onUpdateName: () => void;
    establishmentDescriptionDraft: string;
    onDescriptionChange: (value: string) => void;
    updatingDescription: boolean;
    onUpdateDescription: () => void;
    locationData: { address: string; latitude: number; longitude: number };
    onLocationChange: (address: string, latitude: number, longitude: number) => void;
    updatingLocation: boolean;
    onUpdateLocation: () => void;
    onLogout: () => void;
}) {
    return (
        <div className="merchant-tab-content">
            <div className="merchant-section-head">
                <h2 className="merchant-section-title merchant-section-title--plain merchant-settings-page-title">Ajustes</h2>
                <p className="merchant-section-desc">{merchant.email}</p>
            </div>

            <MerchantPanel title="Nome do estabelecimento" description="O nome aparece no app para os turistas." icon={<Building2 size={20} aria-hidden />}>
                <input
                    type="text"
                    className="merchant-text-input"
                    value={establishmentNameDraft}
                    onChange={(e) => onEstablishmentNameChange(e.target.value)}
                    placeholder="Nome do estabelecimento"
                    autoComplete="organization"
                    aria-label="Nome do estabelecimento"
                />
                <div className="merchant-input-with-icon">
                    <Lock size={18} className="merchant-input-leading-icon" aria-hidden />
                    <input
                        type={showNameChangePassword ? 'text' : 'password'}
                        className="merchant-text-input merchant-text-input--padded"
                        value={nameChangePassword}
                        onChange={(e) => onNameChangePasswordChange(e.target.value)}
                        placeholder="Senha da conta para confirmar"
                        autoComplete="current-password"
                    />
                    <button
                        type="button"
                        className="merchant-input-suffix-btn"
                        aria-label={showNameChangePassword ? 'Ocultar senha' : 'Mostrar senha'}
                        onClick={onToggleNamePassword}
                    >
                        {showNameChangePassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                </div>
                <div className="merchant-actions-row">
                    <Button onClick={onUpdateName} disabled={updatingName}>{updatingName ? 'Salvando...' : 'Salvar nome'}</Button>
                </div>
            </MerchantPanel>

            <MerchantPanel title="Descrição para os turistas" description="Texto exibido na seção Sobre da página do seu estabelecimento." icon={<FileText size={20} aria-hidden />}>
                <textarea
                    className="merchant-textarea"
                    value={establishmentDescriptionDraft}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                    placeholder="Conte o que o visitante encontra no seu espaço, diferenciais, horários úteis..."
                    rows={5}
                    maxLength={2000}
                    aria-label="Descrição do estabelecimento"
                />
                <p className="merchant-char-hint">{establishmentDescriptionDraft.length}/2000</p>
                <div className="merchant-actions-row">
                    <Button onClick={onUpdateDescription} disabled={updatingDescription}>{updatingDescription ? 'Salvando...' : 'Salvar descrição'}</Button>
                </div>
            </MerchantPanel>

            <MerchantPanel title="Endereço e localização" description="Mantenha o endereço atualizado para os turistas encontrarem seu estabelecimento." icon={<MapPin size={20} aria-hidden />}>
                <AddressPicker
                    initialAddress={locationData.address}
                    initialLat={locationData.latitude}
                    initialLng={locationData.longitude}
                    onSelect={onLocationChange}
                />
                <div className="merchant-actions-row merchant-actions-row--tight-top">
                    <Button onClick={onUpdateLocation} disabled={updatingLocation}>{updatingLocation ? 'Salvando...' : 'Salvar localização'}</Button>
                </div>
            </MerchantPanel>

            <div className="merchant-settings-logout">
                <button type="button" className="merchant-logout-footer-btn" onClick={onLogout} aria-label="Sair da conta">
                    Sair da conta
                </button>
            </div>
        </div>
    );
}

export function MerchantPixQrModal({
    isOpen,
    pixPayment,
    pixCopied,
    onClose,
    onCopy,
}: {
    isOpen: boolean;
    pixPayment: PixPaymentPayload | null;
    pixCopied: boolean;
    onClose: () => void;
    onCopy: () => void;
}) {
    const pixStatus = pixPayment ? platformPaymentStatusLabel(pixPayment.status) : null;
    const PixStatusIcon = pixStatus?.Icon ?? Clock;
    const pixStatusText = pixPayment?.status === 'approved' ? 'Pagamento confirmado' : pixStatus?.text ?? 'Aguardando pagamento';

    return (
        <Modal
            isOpen={isOpen && !!pixPayment}
            onClose={onClose}
            title="QR Code Pix"
            contentClassName="merchant-pix-qr-shell"
            footer={(
                <>
                    <Button variant="ghost" onClick={onClose}>Fechar</Button>
                    <Button variant="primary" onClick={onCopy}>
                        {pixCopied ? <><Check size={18} /> Copiado</> : <><Copy size={18} /> Copiar código Pix</>}
                    </Button>
                </>
            )}
        >
            {pixPayment && (
                <div className="pix-payment-modal pix-payment-modal--dedicated">
                    <div className="pix-payment-summary">
                        <span className="pix-payment-label">Valor da taxa</span>
                        <strong>{money(pixPayment.amount)}</strong>
                        <span className={`pix-payment-status pix-payment-status--${pixStatus?.tone ?? 'pending'}`}>
                            <PixStatusIcon size={16} /> {pixStatusText}
                        </span>
                    </div>

                    <div className="pix-payment-qr">
                        {pixPayment.qrCodeBase64 ? (
                            <img src={`data:image/png;base64,${pixPayment.qrCodeBase64}`} alt="QR Code Pix" />
                        ) : (
                            <QRCode value={pixPayment.qrCode || ''} size={220} style={{ height: 'auto', maxWidth: '100%', width: '100%' }} />
                        )}
                    </div>

                    <div className="pix-payment-code-box">
                        <span>Código copia e cola</span>
                        <code>{pixPayment.qrCode}</code>
                    </div>

                    {pixPayment.expiresAt && <p className="pix-payment-expires">Vence em {new Date(pixPayment.expiresAt).toLocaleString('pt-BR')}</p>}
                    {pixPayment.ticketUrl && (
                        <a className="pix-payment-link" href={pixPayment.ticketUrl} target="_blank" rel="noreferrer">
                            Abrir comprovante no Mercado Pago
                        </a>
                    )}
                </div>
            )}
        </Modal>
    );
}
